// DraggableFeatureList.js
import { useState } from "react";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Box, Paper, Typography, Stack } from "@mui/material";
import { CustomSwitch } from "./ToggleSection";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

function SortableItem({ item, id, onToggle }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        visibility: isDragging ? "hidden" : "visible",
      }}
      sx={{
        px: 1.5,
        py: 1.2,
        borderRadius: "10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        bgcolor: "#fff",
        border: "1px solid #e8ecf1",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flex={1}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: "grab",
            display: "flex",
            alignItems: "center",
            color: "#b0b8c4",
            "&:hover": { color: "#7c8694" },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
          {item.label}
        </Typography>
      </Stack>

      <CustomSwitch checked={item.enabled} onChange={() => onToggle(id, "enabled")} />
    </Box>
  );
}

function Column({ id, title, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: 1,
        p: 1.5,
        borderRadius: "12px",
        border: isOver ? "1.5px dashed #94a3b8" : "1.5px solid #e8ecf1",
        backgroundColor: isOver ? "rgba(0,0,0,0.02)" : "#fff",
        minHeight: 200,
        transition: "all 0.2s ease",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          px: 0.5,
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function DragPreview({ item }) {
  if (!item) return null;
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.2,
        borderRadius: "10px",
        bgcolor: "#fff",
        border: "1px solid #e8ecf1",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        fontSize: 13,
        fontWeight: 600,
        color: "#334155",
      }}
    >
      {item.label}
    </Box>
  );
}

export default function DraggableFeatureList({
  items,
  getId,
  onReorder,
  onToggle,
}) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ❗ FIX: Must sort by "order"
  const left = items
    .filter((i) => i.position === "left")
    .sort((a, b) => a.order - b.order);

  const middle = items
    .filter((i) => i.position === "middle")
    .sort((a, b) => a.order - b.order);

  const right = items
    .filter((i) => i.position === "right")
    .sort((a, b) => a.order - b.order);

  const findItem = (id) => items.find((i) => getId(i) === id);

  const getColumnFromId = (id) => {
    if (id === "col-left") return "left";
    if (id === "col-middle") return "middle";
    if (id === "col-right") return "right";
    return null;
  };

  const getColumnItems = (column) => {
    if (column === "left") return left;
    if (column === "middle") return middle;
    if (column === "right") return right;
    return [];
  };

  return (
    <Box sx={{ width: "100%" }}>
      <DndContext
        sensors={sensors}
        collisionDetection={(args) => {
          const pointerHits = pointerWithin(args);
          if (pointerHits.length > 0) return pointerHits;

          const rectHits = rectIntersection(args);
          if (rectHits.length > 0) return rectHits;

          return closestCenter(args);
        }}
        onDragStart={({ active }) => setActiveId(active.id)}
        onDragEnd={({ active, over }) => {
          setActiveId(null);
          if (!over) return;

          const activeItem = findItem(active.id);
          if (!activeItem) return;

          const fromColumn = activeItem.position;

          // Fix: Detect target column from column ID or from the item being dropped on
          const overItem = findItem(over.id);
          const toColumn = getColumnFromId(over.id) || overItem?.position || fromColumn;

          const sourceList = getColumnItems(fromColumn);
          const targetList = getColumnItems(toColumn);

          const oldIndex = sourceList.findIndex((i) => getId(i) === active.id);

          let newIndex = targetList.findIndex((i) => getId(i) === over.id);

          // Fix: Handle dropping on column container or empty column
          if (newIndex === -1) {
            newIndex = targetList.length;
          }

          if (fromColumn !== toColumn) {
            // Build the new order for target column with the dragged item inserted at the correct position
            const targetIds = targetList.map((i) => getId(i));
            targetIds.splice(newIndex, 0, active.id);

            // Update position to new column, then reorder target column
            onToggle(active.id, "position", toColumn);
            onReorder(toColumn, targetIds);
            return;
          }

          if (oldIndex === newIndex) return;

          const orderedIds = arrayMove(
            sourceList.map((i) => getId(i)),
            oldIndex,
            newIndex
          );

          onReorder(fromColumn, orderedIds);
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <Column id="col-left" title={`LEFT (${left.length})`}>
            <SortableContext
              items={left.map(getId)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1} mt={1}>
                {left.map((item) => (
                  <SortableItem
                    key={getId(item)}
                    id={getId(item)}
                    item={item}
                    onToggle={onToggle}
                  />
                ))}
              </Stack>
            </SortableContext>
          </Column>

          <Column id="col-middle" title={`MIDDLE (${middle.length})`}>
            <SortableContext
              items={middle.map(getId)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1} mt={1}>
                {middle.map((item) => (
                  <SortableItem
                    key={getId(item)}
                    id={getId(item)}
                    item={item}
                    onToggle={onToggle}
                  />
                ))}
              </Stack>
            </SortableContext>
          </Column>

          <Column id="col-right" title={`RIGHT (${right.length})`}>
            <SortableContext
              items={right.map(getId)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1} mt={1}>
                {right.map((item) => (
                  <SortableItem
                    key={getId(item)}
                    id={getId(item)}
                    item={item}
                    onToggle={onToggle}
                  />
                ))}
              </Stack>
            </SortableContext>
          </Column>
        </Box>

        <DragOverlay>
          <DragPreview item={findItem(activeId)} />
        </DragOverlay>
      </DndContext>
    </Box>
  );
}
